import useOffers from 'hooks/Offers/useOffers'
import useOrder from 'hooks/Order/useOrder'
import { usePlaceByCoords } from 'hooks/usePlace'
import { getOrderItemsEligibleForCarHireBundledDiscount } from 'lib/carHire/carHireUtils'
import { useMemo } from 'react'
import useCarHireOfferListFilters from './useCarHireOfferListFilters'
import { useAppSelector } from 'hooks/reduxHooks'
import { getDefaultUserDriverCategory } from 'selectors/carHireSelectors'
import { findCarHireListKey, getCarHireListKey } from 'components/CarHire/carHireUtils'

const EMPTY_ITEMS_RESULT = {
  earliestItem: undefined,
  latestItem: undefined,
  items: [],
}

function useOrderItemsEligibleForCarHireBundledDiscount(orderId?: string) {
  const [order, isFetchingOrder] = useOrder(orderId)

  const offerIds = useMemo(() => {
    if (!order) return []
    return order.items.map((i) => i.offer.id)
  }, [order])

  const [offers, isFetchingOffers] = useOffers(offerIds)

  const [defaultAgeCategory, defaultAge] = useAppSelector(getDefaultUserDriverCategory)

  const itemsResult = useMemo(() => {
    if (!order) {
      return EMPTY_ITEMS_RESULT
    }

    return getOrderItemsEligibleForCarHireBundledDiscount(order, offers)
  }, [order, offers])

  const [place, fetchingPlace] = usePlaceByCoords(itemsResult.earliestItem?.latitude, itemsResult.earliestItem?.longitude)

  const filters = useCarHireOfferListFilters({
    pickUpLocationId: place?.id,
    dropOffLocationId: place?.id,
    pickUpDate: itemsResult.earliestItem?.startDate || undefined,
    dropOffDate: itemsResult.latestItem?.endDate || undefined,
    pickUpTime: '10:00',
    dropOffTime: '10:00',
    driversAgeCategory: defaultAgeCategory,
    isPickUpAtAirport: false,
    pickUpSearchType: 'geo',
    dropOffSearchType: 'geo',
    driversAge: defaultAge,
    bundledOrderId: order?.id,
  })

  const listKey = useMemo(() => getCarHireListKey(filters), [filters])
  const { vehicles, fetching: fetchingVehicles } = useAppSelector(state => findCarHireListKey(state.carHire.offerLists, listKey) ?? { vehicles: [], fetching: true })
  const hasVehicles = vehicles.length > 0

  return {
    ...(hasVehicles || fetchingVehicles ? itemsResult : EMPTY_ITEMS_RESULT),
    place,
    fetching: isFetchingOrder || isFetchingOffers || fetchingPlace || fetchingVehicles,
  }
}

export default useOrderItemsEligibleForCarHireBundledDiscount
